import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { translate } from 'react-i18next'

import { renderToastWarn } from 'utils/ToastUtils'
import { ToastContainer } from 'react-toastify'
import Breadcrumb from 'component/Breadcrumb'
import Col from 'component/utils/Col'
import ContentHeader from 'component/utils/ContentHeader'
import FilterForm from 'component/stockTransfer/MissionBlockFilterForm'
import Grid from 'component/utils/Grid'
import MissionBlockAddModal from 'component/stockTransfer/MissionBlockAddModal'
import MissionBlockEditModal from 'component/stockTransfer/MissionBlockEditModal'
import MissionBlockRemoveModal from 'component/stockTransfer/MissionBlockRemoveModal'
import MissionBlockSuccessModal from 'component/stockTransfer/MissionBlockSuccessModal'
import Notification from 'component/utils/Notification'
import Pagination from 'component/pagination/Pagination'
import SideFilterForm from 'component/stockTransfer/MissionBlockSideFilterForm'
import Table from 'component/stockTransfer/MissionBlockTable'
import Title from 'component/stockTransfer/MissionBlockTitle'

import * as stockTransferReportActions from 'action/stockTransferReport'
import * as depositActions from 'action/deposit'

const DEFAULT_PAGINATION = {
  hasPagination: true,
  page: 1,
  size: 10,
}

const DEFAULT_INITIAL_VALUES = {
  dsMission: '',
  idSourceDepositList: [],
  idDestinationDepositList: [],
}

const EXTRA_SMALL_SCREEN = 768

const MissionBlockContainer = ({
  auth: {
    user: { dsToken },
  },
  count,
  current,
  depositList,
  fetchDepositList,
  fetchMissionDepositBlock,
  missionBlockError,
  missionBlockList,
  pages,
  saveMissionDepositBlock,
  translate,
}) => {
  const [openAddModal, setOpenAddModal] = useState(false)
  const [openEditModal, setOpenEditModal] = useState(null)
  const [openRemoveModal, setOpenRemoveModal] = useState(false)
  const [openSuccessModal, setOpenSuccessModal] = useState(false)

  const [cdMissionList, setCdMissionList] = useState([])

  const headerRef = useRef(null)
  const filterRef = useRef(null)
  const tableRef = useRef(null)

  const [filters, setFilters] = useState(DEFAULT_INITIAL_VALUES)
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION)
  const [sideFilterElement, setSideFilterElement] = useState(null)
  const [sideFilterVisible, setSideFilterVisible] = useState(false)
  const [sideFilterWidth, setSideFilterWidth] = useState(0)
  const [topMargin, setTopMargin] = useState(0)

  // Did mount
  useEffect(() => {
    fetchDepositList(dsToken)

    window.addEventListener('resize', handleResize)
  }, [])

  // When error changes
  useEffect(() => {
    if (missionBlockError) renderToastWarn(missionBlockError)
  }, [missionBlockError])

  // When filters change
  useEffect(() => {
    handleResize()

    const param = {
      ...filters,
      ...DEFAULT_PAGINATION,
    }

    fetchMissionDepositBlock(dsToken, param)
  }, [filters])

  // When pagination changes
  useEffect(() => {
    handleResize()

    handleFetchMissionDepositBlock()
  }, [pagination])

  // Will unmount
  useEffect(() => () => window.removeEventListener('resize', handleResize))

  const handleResize = () => {
    const header = headerRef.current

    if (header && window.innerWidth > EXTRA_SMALL_SCREEN) {
      const headerClientHeight = header.clientHeight

      const filter = filterRef.current

      const topHeight = headerClientHeight + filter.clientHeight

      let newSideFilterWidth = sideFilterWidth

      filter.style.top = `${headerClientHeight}px`

      if (sideFilterVisible && sideFilterElement)
        newSideFilterWidth = sideFilterElement.clientWidth

      if (topMargin !== topHeight || sideFilterWidth !== newSideFilterWidth) {
        setSideFilterWidth(newSideFilterWidth)

        setTopMargin(topHeight)
      }
    }
  }

  const handleFetchMissionDepositBlock = () => {
    const param = {
      ...filters,
      ...pagination,
    }

    fetchMissionDepositBlock(dsToken, param)
  }

  const renderBreadcrumb = () => {
    const steps = [
      { title: translate('mobile.home'), link: '/' },
      { title: translate('stockTransfer.missionBlock'), strong: true },
    ]

    return <Breadcrumb steps={steps} />
  }

  const setValues = (values) => {
    setFilters({ ...filters, ...values, o: Math.random })
  }

  const renderPagination = () => {
    if (!!missionBlockList.length && pages < 2) return

    return (
      <Col className='align-center rupture-irc-pagination'>
        <Pagination
          count={count}
          current={current}
          getPage={(page) => setPagination({ ...pagination, page })}
          pages={pages}
        />
      </Col>
    )
  }

  const renderEditModal = () => {
    if (!openEditModal) return

    const getInitialValues = () => {
      const getDepositIds = (depositList) =>
        depositList.map((deposit) => deposit.idDeposit)

      const initialValues = {
        dsMission: openEditModal.dsMission,
        idSourceDepositList: getDepositIds(openEditModal.sourceDepositList),
        idDestinationDepositList: getDepositIds(
          openEditModal.destinationDepositList
        ),
      }

      return initialValues
    }

    return (
      <MissionBlockEditModal
        closeModal={() => setOpenEditModal(null)}
        depositList={depositList}
        initialValues={getInitialValues()}
        submit={beforeSave}
        translate={translate}
      />
    )
  }

  const renderAddModal = () => {
    if (!openAddModal) return

    return (
      <MissionBlockAddModal
        cdMissionListLength={cdMissionList.length}
        closeModal={() => setOpenAddModal(false)}
        depositList={depositList}
        initialValues={{
          idSourceDepositList: [],
          idDestinationDepositList: [],
        }}
        submit={beforeSave}
        translate={translate}
      />
    )
  }

  const renderRemoveModal = () => {
    if (!openRemoveModal) return

    return (
      <MissionBlockRemoveModal
        cdMissionListLength={cdMissionList.length}
        closeModal={() => setOpenRemoveModal(false)}
        depositList={depositList}
        initialValues={{
          idSourceDepositList: [],
          idDestinationDepositList: [],
        }}
        submit={beforeSave}
        translate={translate}
      />
    )
  }

  const renderSuccessModal = () => {
    if (!openSuccessModal) return

    return (
      <MissionBlockSuccessModal
        closeModal={() => setOpenSuccessModal(false)}
        translate={translate}
      />
    )
  }

  const beforeSave = async (values) => {
    const param = {
      ...values,
      cdMissionList: openEditModal ? [openEditModal.cdMission] : cdMissionList,
    }

    setOpenAddModal(false)
    setOpenEditModal(null)
    setOpenRemoveModal(false)
    setCdMissionList([])

    await saveMissionDepositBlock(dsToken, param)

    setOpenSuccessModal(true)

    const fetchParam = {
      ...filters,
      ...DEFAULT_PAGINATION,
    }

    fetchMissionDepositBlock(dsToken, fetchParam)
  }

  return (
    <div style={{paddingTop: '80px'}} className='stock-adjustment'>
      <div ref={headerRef} className='stock-adjustment-header'>
        <ContentHeader>{translate('stockTransfer.missionBlock')}</ContentHeader>
        {renderBreadcrumb()}
      </div>
      <div style={{display: "none"}} ref={filterRef} className='padding-bottom stock-adjustment-filter'>
        <Grid fluid>
          <Title
            cdMissionListLength={cdMissionList.length}
            handleSideFilterMenuCollapseChange={setSideFilterVisible}
            setCdMissionList={setCdMissionList}
            setOpenAddModal={setOpenAddModal}
            setOpenRemoveModal={setOpenRemoveModal}
            sideFilterVisible={sideFilterVisible}
            translate={translate}
          />
          <FilterForm submit={setValues} translate={translate} />
        </Grid>
      </div>
      <div
        style={{ margin: '0 !important' }}
        ref={tableRef}
        className='stock-adjustment-table'
      >
        <Grid fluid className='no-padding'>
          <div className='outer-table'>
            <div className='flex'>
              <SideFilterForm
                depositList={depositList}
                handleSideFilterElementChange={setSideFilterElement}
                initialValues={DEFAULT_INITIAL_VALUES}
                sideFilterVisible={sideFilterVisible}
                submit={setValues}
                translate={translate}
              />
              {missionBlockList.length ? (
                <Table
                  cdMissionList={cdMissionList}
                  handleResize={handleResize}
                  openModal={setOpenEditModal}
                  records={missionBlockList}
                  setCdMissionList={setCdMissionList}
                  translate={translate}
                />
              ) : (
                <Col
                  xs={12}
                  className='margin-top margin-right'
                  style={{ marginLeft: `${sideFilterVisible ? 0.5 : 1}rem` }}
                >
                  <Notification
                    message={translate('no.data.found')}
                    className='notification-warning'
                  />
                </Col>
              )}
            </div>
            {renderPagination()}
          </div>
        </Grid>
      </div>
      {renderEditModal()}
      {renderAddModal()}
      {renderRemoveModal()}
      {renderSuccessModal()}
      <ToastContainer newestOnTop />
    </div>
  )
}

MissionBlockContainer.propTypes = {
  auth: PropTypes.object.isRequired,
  count: PropTypes.number.isRequired,
  current: PropTypes.number.isRequired,
  depositList: PropTypes.array.isRequired,
  fetchDepositList: PropTypes.func.isRequired,
  fetchMissionDepositBlock: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired,
  menu: PropTypes.object.isRequired,
  missionBlockError: PropTypes.object,
  missionBlockList: PropTypes.array.isRequired,
  pages: PropTypes.number.isRequired,
  saveMissionDepositBlock: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
}

/* istanbul ignore next */
const mapStateToProps = ({
  stockTransferReportReducer,
  depositReducer,
  paginateReducer,
}) => ({
  ...stockTransferReportReducer,
  ...paginateReducer,
  depositList: depositReducer.list,
})

/* istanbul ignore next */
const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      ...stockTransferReportActions,
      fetchDepositList: depositActions.fetch,
    },
    dispatch
  )

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  translate(null, { wait: true, translateFuncName: 'translate' })(
    MissionBlockContainer
  )
)

export { MissionBlockContainer }
